/**
 * Phira's in-line note order and culling groups (W1-06; R§4.5 item 7, R§4.8, F§11.5, A§7.2).
 *
 * Each line keeps its notes sorted by the key `(plain, !above, speed, (height + yOff) * speed)`, stable on file
 * order, where `plain` = real (non-fake) non-hold note and `yOff = yOffset * 2/900 * speed` (the note's stored
 * offset, world units before the aspect division). The render loop walks one side at a time: the non-plain notes
 * of that side (holds and fakes, always iterated) and then the plain groups of that side, where a group is a
 * maximal run of plain notes with the same side and the bit-identical f32 speed. That walk gives Phira's push
 * order: non-plain above -> plain above -> non-plain below -> plain below.
 */

const fr = Math.fround;

/** f32 sort key `(height + yOff) * speed`, every operation rounded to f32 as Phira computes it. */
export function phiraSortKey(height: number, yOff: number, speed: number): number {
  return fr(fr(height + yOff) * speed);
}

/**
 * Total order on numbers used for the float components of the key: ascending, NaN after every number (a NaN
 * key cannot be ordered by Phira either; this only keeps the sort deterministic).
 */
export function compareKey(a: number, b: number): number {
  if (a < b) return -1;
  if (a > b) return 1;
  if (a === b) return 0;
  const an = Number.isNaN(a);
  const bn = Number.isNaN(b);
  return an === bn ? 0 : an ? 1 : -1;
}

/** Inputs of the order, in file order. */
export interface SortInput {
  readonly n: number;
  readonly plain: Uint8Array;
  readonly above: Uint8Array;
  readonly speed: Float32Array;
  readonly key: Float32Array;
}

/** Permutation `order[k]` = file index of the k-th note in Phira order (stable on file order). */
export function phiraOrder(input: SortInput): Int32Array {
  const { n, plain, above, speed, key } = input;
  const idx: number[] = [];
  for (let i = 0; i < n; i++) idx.push(i);
  idx.sort((a, b) => {
    const pa = plain[a] ?? 0;
    const pb = plain[b] ?? 0;
    if (pa !== pb) return pa - pb;
    // !above ascending: above (1) first.
    const aa = above[a] ?? 0;
    const ab = above[b] ?? 0;
    if (aa !== ab) return ab - aa;
    const s = compareKey(speed[a] ?? 0, speed[b] ?? 0);
    if (s !== 0) return s;
    const k = compareKey(key[a] ?? 0, key[b] ?? 0);
    if (k !== 0) return k;
    return a - b;
  });
  return Int32Array.from(idx);
}

/** Section boundaries and plain groups of a sorted line. All ranges are half-open [start, end). */
export interface NoteSections {
  /** Non-plain above notes: [0, nonPlainAboveEnd). */
  readonly nonPlainAboveEnd: number;
  /** Non-plain below notes: [nonPlainAboveEnd, nonPlainEnd); plain notes start at nonPlainEnd. */
  readonly nonPlainEnd: number;
  /** Plain groups: notes [groupStart[g], groupEnd[g]). Above groups are [0, aboveGroupCount). */
  readonly groupStart: Int32Array;
  readonly groupEnd: Int32Array;
  readonly groupCount: number;
  readonly aboveGroupCount: number;
}

/** Computes the sections of notes already in Phira order (`plain`, `above`, `speed` in sorted order). */
export function sectionsOf(n: number, plain: Uint8Array, above: Uint8Array, speed: Float32Array): NoteSections {
  let nonPlainEnd = 0;
  while (nonPlainEnd < n && plain[nonPlainEnd] === 0) nonPlainEnd++;
  let nonPlainAboveEnd = 0;
  while (nonPlainAboveEnd < nonPlainEnd && above[nonPlainAboveEnd] === 1) nonPlainAboveEnd++;
  const starts: number[] = [];
  const ends: number[] = [];
  let aboveGroupCount = 0;
  let i = nonPlainEnd;
  while (i < n) {
    const side = above[i];
    const sp = speed[i];
    let j = i + 1;
    while (j < n && above[j] === side && speed[j] === sp) j++;
    starts.push(i);
    ends.push(j);
    if (side === 1) aboveGroupCount++;
    i = j;
  }
  return {
    nonPlainAboveEnd,
    nonPlainEnd,
    groupStart: Int32Array.from(starts),
    groupEnd: Int32Array.from(ends),
    groupCount: starts.length,
    aboveGroupCount,
  };
}
